import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Route, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LoggerService } from './logger.service';
import { TranslationService } from './translation.service';

export interface BreadcrumbItem {
  label: string;
  url: string | null;
  isMenuRoot?: boolean;
}

interface BreadcrumbRouteMeta {
  label: string;
  parent?: string | null;
}

/**
 * Builds the breadcrumb trail from the current route by reading
 * `data.breadcrumb` metadata from the route config.
 */
@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly logger = inject(LoggerService).getLogger('breadcrumb');
  private readonly translationService = inject(TranslationService);

  private readonly _breadcrumbsBase = signal<BreadcrumbItem[]>([]);
  /**
   * Dynamic labels for `:dynamic` breadcrumb entries, in left-to-right order.
   * Each label replaces the corresponding `:dynamic` entry in the trail.
   * Use setDynamicLabel() for the common single-label case, or setDynamicLabels()
   * when a page has multiple `:dynamic` ancestors (e.g. parent record name + child record name).
   */
  private readonly _dynamicLabels = signal<string[]>([]);
  /** Optional extra item appended to the breadcrumb trail (e.g. "Edit Field" when modal is open). */
  private readonly _extraItem = signal<BreadcrumbItem | null>(null);
  /** The breadcrumb route data matched on the last rebuild(), used to tell a
   *  genuine page change apart from a same-page, query-param-only navigation
   *  (e.g. a wizard's `?step=`) — see {@link rebuild}. */
  private lastRouteData: BreadcrumbRouteMeta | null = null;

  /** The full breadcrumb trail, starting with the ☰ menu root item.
   *  All `:dynamic` label entries are replaced left-to-right with the values
   *  provided via setDynamicLabel() / setDynamicLabels().
   *  An optional extra item (e.g. modal context) is appended when set.
   */
  readonly breadcrumbs: Signal<BreadcrumbItem[]> = computed(() => {
    const base = this._breadcrumbsBase();
    const dynamicLabels = this._dynamicLabels();
    const extraItem = this._extraItem();

    let resolved: BreadcrumbItem[];
    if (dynamicLabels.length === 0 || base.length === 0) {
      resolved = base;
    } else {
      let labelIndex = 0;
      resolved = base.map((item) => {
        if (item.label === ':dynamic' && labelIndex < dynamicLabels.length) {
          return { ...item, label: dynamicLabels[labelIndex++] };
        }
        return item;
      });
    }

    if (extraItem) {
      return [...resolved, extraItem];
    }
    return resolved;
  });

  constructor() {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.rebuild();
    });
  }

  private rebuild(): void {
    const currentUrl = this.router.url;
    const urlWithoutQuery = currentUrl.split('?')[0];

    // Collect route params from the entire activated route tree
    const params = this.collectParams(this.activatedRoute.root);
    const routeData = this.findRouteData(urlWithoutQuery, params);

    // A query-param-only navigation (e.g. a same-page filter or step param)
    // re-fires NavigationEnd for the same page — only reset labels a
    // component set via setDynamicLabel()/setDynamicLabels() (and the extra
    // item) when the matched route actually changes, so they survive it.
    if (routeData !== this.lastRouteData) {
      this._dynamicLabels.set([]);
      this._extraItem.set(null);
    }
    this.lastRouteData = routeData;

    // Walk the activated route tree to collect breadcrumb data
    const items = this.buildTrail(urlWithoutQuery, params, routeData);

    this._breadcrumbsBase.set(items);
    this.logger.debug('Breadcrumbs rebuilt:', items);
  }

  /**
   * Called by page components to set the dynamic breadcrumb label for the
   * current page (the last `:dynamic` entry in the trail).
   * Convenience wrapper for the common single-label case.
   * The route's breadcrumb label must be `:dynamic` for this to take effect.
   */
  setDynamicLabel(label: string | null): void {
    this._dynamicLabels.set(label ? [label] : []);
  }

  /**
   * Called by page components that need to resolve multiple `:dynamic` breadcrumb
   * entries in the trail (e.g. a page nested under two dynamic ancestors).
   * Labels are applied left-to-right to each `:dynamic` entry in the trail.
   *
   * Example: for route `/addresses/:addressId/history/:entryId` the trail contains
   * two `:dynamic` entries — the address label and the history entry label:
   *   setDynamicLabels(['123 Main St', 'Edit #4'])
   */
  setDynamicLabels(labels: string[]): void {
    this._dynamicLabels.set(labels);
  }

  /**
   * Append an extra breadcrumb item to the trail (e.g. "Edit Field" when a modal is open).
   * Call clearExtraItem() when the context is dismissed.
   */
  setExtraItem(item: BreadcrumbItem): void {
    this._extraItem.set(item);
  }

  /** Remove the extra breadcrumb item (restores the trail to its normal state). */
  clearExtraItem(): void {
    this._extraItem.set(null);
  }

  private collectParams(route: ActivatedRoute): Params {
    let params: Params = {};
    let current: ActivatedRoute | null = route;
    while (current) {
      params = { ...params, ...current.snapshot.params };
      current = current.firstChild;
    }
    return params;
  }

  /** `currentUrl` must already have its query string stripped. */
  private buildTrail(
    currentUrl: string,
    params: Params,
    routeData: BreadcrumbRouteMeta | null,
  ): BreadcrumbItem[] {
    if (!routeData) {
      // No breadcrumb data — just show the menu root
      return [this.menuRootItem()];
    }

    // Build ancestor chain by following parent references
    const chain: BreadcrumbItem[] = [];
    let current: { label: string; parent?: string | null } | null = routeData;
    let currentUrlResolved: string | null = currentUrl;
    let depth = 0;

    while (current && depth < 10) {
      const isLast = depth === 0;
      chain.unshift({
        // ':dynamic' is a sentinel replaced later (see `breadcrumbs` computed)
        // with a value a page component sets via setDynamicLabel() -- it isn't
        // an i18n key, so it must not be run through translate().
        label: current.label === ':dynamic' ? current.label : this.translationService.translate(current.label),
        url: isLast ? null : this.resolveUrl(currentUrlResolved ?? '', params),
      });

      if (!current.parent) break;
      currentUrlResolved = this.resolveUrl(current.parent, params);

      // Find data for the parent URL
      const parentData = this.findRouteData(currentUrlResolved, params);
      current = parentData;
      depth++;
    }

    return [this.menuRootItem(), ...chain];
  }

  private menuRootItem(): BreadcrumbItem {
    return { label: '☰', url: null, isMenuRoot: true };
  }

  /**
   * Resolve a URL template (e.g. "/addresses/:addressId") using current route params.
   */
  private resolveUrl(urlTemplate: string, params: Params): string {
    return urlTemplate.replace(/:([a-zA-Z]+)/g, (_, key) => params[key] ?? `:${key}`);
  }

  /**
   * Find the breadcrumb route data for a resolved URL.
   * Tries to match against the route config using the current params.
   * Walks `route.children` recursively, accumulating the path prefix as it
   * descends — routes no longer have to be top-level entries (e.g. everything
   * nested under `/map`) for their `data.breadcrumb` to be found.
   */
  private findRouteData(resolvedUrl: string, params: Params): BreadcrumbRouteMeta | null {
    return this.findRouteDataIn(this.router.config, '', resolvedUrl, params);
  }

  private findRouteDataIn(
    routes: Route[],
    prefix: string,
    resolvedUrl: string,
    params: Params,
  ): BreadcrumbRouteMeta | null {
    for (const route of routes) {
      const segment = route.path ? `${prefix}/${route.path}` : prefix;

      if (route.data?.['breadcrumb']) {
        const routePattern = segment || '/';
        const resolved = this.resolveUrl(routePattern, params);
        if (resolved === resolvedUrl || this.matchesPattern(routePattern, resolvedUrl)) {
          return route.data['breadcrumb'] as BreadcrumbRouteMeta;
        }
      }

      if (route.children) {
        const found = this.findRouteDataIn(route.children, segment, resolvedUrl, params);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Check if a URL matches a route pattern with params (e.g. "/addresses/:id").
   */
  private matchesPattern(pattern: string, url: string): boolean {
    // Convert route pattern to regex: /addresses/:id -> ^\/addresses\/[^/]+$
    const escaped = pattern.replace(/:[a-zA-Z]+/g, '[^/]+').replace(/\//g, '\\/');
    const regex = new RegExp(`^${escaped}$`);
    return regex.test(url);
  }
}
